import { createTheme } from '@mui/material/styles'

// Extend MUI theme types
declare module '@mui/material/styles' {
    interface Palette {
        background1: Palette['primary']
        background2: Palette['primary']
        background3: Palette['primary']
        disabled: Palette['primary']
    }
    interface PaletteOptions {
        background1?: PaletteOptions['primary']
        background2?: PaletteOptions['primary']
        background3?: PaletteOptions['primary']
        disabled?: PaletteOptions['primary']
    }
    interface TypographyVariants {
        body3: React.CSSProperties
        caption2: React.CSSProperties
        link: React.CSSProperties
    }
    interface TypographyVariantsOptions {
        body3?: React.CSSProperties
        caption2?: React.CSSProperties
        link?: React.CSSProperties
    }
    interface TypographyVariantsOptions {
        link?: React.CSSProperties
    }
}

// Extend Typography props
declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        body3: true
        caption2: true
        link: true
    }
}

const Theme = createTheme({
    palette: {
        primary: {
            main: 'rgba(0, 59, 111, 1)',
            light: 'rgba(90, 143, 195, 1)',
            dark: 'rgba(56, 66, 80, 1)'
        },
        secondary: {
            main: 'rgba(255, 255, 255, 1)',
            light: 'rgba(204, 222, 239, 1)',
            dark: 'rgba(112, 119, 131, 1)'
        },
        warning: {
            main: 'rgba(255, 100, 14, 1)',
            light: 'rgba(255, 236, 215, 1)',
            dark: 'rgba(255, 191, 58, 1)'
        },
        error: {
            main: 'rgba(254, 93, 124, 1)',
            light: 'rgba(255, 231, 235, 1)'
        },
        success: {
            main: 'rgba(111, 207, 151, 1)',
            light: 'rgba(204, 222, 239, 1)',
            dark: 'rgba(3, 195, 2, 1)'
        },
        disabled: {
            main: 'rgba(245, 245, 245, 1)',
            light: 'rgba(56, 66, 80, 0.5)'
        },
        background: {
            default: 'rgba(255, 255, 255, 1)'
        },
        background1: {
            main: 'rgba(0, 59, 111, 1)',
            light: 'rgba(175, 190, 213, 1)'
        },
        background2: {
            main: 'rgba(153, 188, 223, 1)',
            light: 'rgba(233, 241, 250, 1)'
        },
        background3: {
            main: 'rgba(244, 248, 251, 1)',
            light: 'rgba(255, 241, 220, 1)'
        }
    },
    typography: {
        fontFamily: 'var(--font-inter)',
        h1: {
            fontSize: '32px',
            fontWeight: 700
        },
        h2: {
            fontSize: '24px',
            fontWeight: 700
        },
        h3: {
            fontSize: '20px',
            fontWeight: 700
        },
        h4: {
            fontSize: '20px',
            fontWeight: 400
        },
        body1: {
            fontSize: '18px',
            fontWeight: 400
        },
        body2: {
            fontSize: '16px',
            fontWeight: 400
        },
        body3: {
            fontSize: '14px',
            fontWeight: 400
        },
        caption: {
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: 1.66
        },
        caption2: {
            fontSize: '12px',
            fontWeight: 400,
            lineHeight: 1.66
        },
        button: {
            fontSize: '20px',
            fontWeight: 500,
            lineHeight: 1.66,
            background: 'primary.main',
            textTransform: 'uppercase'
        },
        link: {
            fontSize: '18px',
            fontWeight: 400,
            textDecoration: 'underline',
            textUnderlineOffset: '4px'
        }
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    fontSize: '22px',
                    fontWeight: 500,
                    lineHeight: 1.66,
                    background: theme.palette.primary.main,
                    textTransform: 'uppercase',
                    borderRadius: '70px',
                    boxShadow: 'none',
                    '&.Mui-disabled': {
                        backgroundColor: theme.palette.secondary.light,
                        color: theme.palette.secondary.main,
                        cursor: 'not-allowed'
                    }
                })
            }
        },
        MuiAccordion: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '&::before': {
                        display: 'none'
                    },
                    '&.Mui-expanded': {
                        margin: '0px!important'
                    },
                    '&.faq-parent': {
                        backgroundColor: theme.palette.secondary.light,
                        borderRadius: '8px!important',
                        '& .MuiAccordionSummary-root': {
                            minHeight: '80px',
                            '& .MuiButtonBase-root': {
                                minHeight: '80px'
                            }
                        }
                    },
                    '&.faq-child': {
                        borderRadius: '8px!important',
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '20px',
                        '&.Mui-expanded': {
                            gap: '20px'
                        },
                        '& .MuiAccordionSummary-root': {
                            alignItems: 'flex-start',
                            gap: '15px',
                            padding: '0px',
                            '& .MuiAccordionSummary-content': {
                                margin: '0px'
                            }
                        },
                        '& .MuiAccordionDetails-root': {
                            padding: '0px'
                        }
                    }
                })
            }
        }
    }
})

export default Theme
